import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Typography, Card, message, Select, DatePicker, Avatar } from 'antd';
import { ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const LoginHistory: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loginHistory, setLoginHistory] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [dateRange, setDateRange] = useState<any>(null);

  useEffect(() => {
    fetchLoginHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, statusFilter, dateRange]);

  const fetchLoginHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('pageId', pagination.current.toString());
      params.set('perPage', pagination.pageSize.toString());
      params.set('sort', 'DESC');
      
      if (statusFilter) {
        params.set('status', statusFilter);
      }
      
      if (dateRange && dateRange[0] && dateRange[1]) {
        const start = dateRange[0].format ? dateRange[0].format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : dateRange[0].toISOString();
        const end = dateRange[1].format ? dateRange[1].format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : dateRange[1].toISOString();
        params.set('startDate', start);
        params.set('endDate', end);
      }

      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_GET_LOGIN_HISTORY}?${params}`);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setLoginHistory(items);
        
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination(prev => ({
            ...prev,
            total: paginator.totalCount || paginator.total || 0,
          }));
        }
      }
    } catch (error: any) {
      message.error('Failed to fetch login history');
      console.error('Error fetching login history:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_: any, record: any) => {
        let user: any = null;
        if (record.administrator) {
          user = record.administrator;
        } else if (record.merchant) {
          user = record.merchant;
        }
        
        if (!user) return '-';
        
        return (
          <Space>
            <Avatar 
              src={user.portraitUrl ? `data:image/png;base64,${user.portraitUrl}` : undefined}
              icon={!user.portraitUrl ? <UserOutlined /> : undefined}
              size="small"
            />
            <span>{user.email}</span>
          </Space>
        );
      },
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (type: string) => <Tag color={type === 'Administrator' ? 'blue' : 'green'}>{type}</Tag>,
    },
    {
      title: 'Email',
      key: 'email',
      render: (_: any, record: any) => {
        if (record.administrator) {
          return record.administrator.email;
        }
        if (record.merchant) {
          return record.merchant.email;
        }
        return '-';
      },
    },
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: any) => {
        if (record.administrator) {
          return `${record.administrator.firstName} ${record.administrator.lastName}`;
        }
        if (record.merchant) {
          return `${record.merchant.firstName} ${record.merchant.lastName}`;
        }
        return '-';
      },
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
    },
    {
      title: 'User Agent',
      dataIndex: 'userAgent',
      key: 'userAgent',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'success' ? 'green' : 'red';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Login Time',
      dataIndex: 'loginTime',
      key: 'loginTime',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Login History</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchLoginHistory}
          loading={loading}
        >
          Refresh
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Status</span>
              <Select
                placeholder="Filter by status"
                value={statusFilter || undefined}
                onChange={(value) => {
                  setStatusFilter(value);
                  const newParams = new URLSearchParams(searchParams);
                  if (value) {
                    newParams.set('status', value);
                  } else {
                    newParams.delete('status');
                  }
                  setSearchParams(newParams);
                }}
                allowClear
                style={{ width: 150 }}
              >
                <Select.Option value="success">Success</Select.Option>
                <Select.Option value="failed">Failed</Select.Option>
              </Select>
            </div>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Date Range</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  setDateRange(dates);
                }}
                style={{ width: 300 }}
              />
            </div>
          </Space>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={loginHistory}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              },
            }}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>
    </PageContainer>
  );
};

export default LoginHistory;

